import {
  Button,
  Descriptions,
  Drawer,
  Input,
  InputNumber,
  Message,
  Select,
  Switch,
} from "@arco-design/web-react";
import { useEffect, useMemo, useState } from "react";
import { Sparkles, X } from "lucide-react";

import {
  GeoOperationPanel,
  type GeoBusinessCandidate,
  type GeoBusinessVerifyResult,
  type GeoOperationStatus,
} from "../../../shared/business-geo";
import { getCityOptions, getDistrictOptions, getProvinceOptions } from "../../../shared/region";
import {
  applyScopeLockedSpuDefaults,
  resourcePointStatusOptions,
  resourcePointSubTypeOptions,
  scopeEntityLabel,
  type ResourcePointListScope,
} from "../config";
import { polishResourcePointShortName } from "../repo";
import {
  emptyResourcePointSpuMutation,
  type ResourcePointMutationResult,
  type ResourcePointSpuMutationErrors,
} from "../runtime";
import {
  applyResourcePointConfirmedGeo,
  applyResourcePointGeoCandidate,
  fillResourcePointMutationFromGeo,
  formatResourcePointDateTime,
  resourcePointMutationErrorMessage,
  toResourcePointSpuMutation,
} from "../service";
import type { ResourcePointSpuItem, ResourcePointSpuMutation } from "../types";
import { ResourcePointFormRow } from "./ResourcePointFormRow";
import { ResourcePointImageField } from "./ResourcePointImageField";

/** 考察点 SPU 抽屉的新增或编辑状态。 */
export type ResourcePointSpuDrawerState =
  | { type: "create" }
  | { type: "edit"; spu: ResourcePointSpuItem };

type ResourcePointSpuDrawerProps = {
  state: ResourcePointSpuDrawerState | null;
  scope?: ResourcePointListScope;
  pointTypes: string[];
  pending: boolean;
  errorMessage: string;
  geoPending: boolean;
  geoStatus: GeoOperationStatus;
  geoResult?: GeoBusinessVerifyResult;
  geoOwnerSpuId?: string;
  onClose: () => void;
  onAfterClose: () => void;
  onClearError: () => void;
  onHydrateGeo: (spu?: ResourcePointSpuItem) => void;
  onVerifyGeo: (
    spu: ResourcePointSpuItem | undefined,
    payload: ResourcePointSpuMutation,
  ) => Promise<GeoBusinessVerifyResult | undefined>;
  onAdoptGeo: () => void;
  onCreate: (
    payload: ResourcePointSpuMutation,
  ) => Promise<ResourcePointMutationResult<ResourcePointSpuMutationErrors>>;
  onUpdate: (
    spu: ResourcePointSpuItem,
    payload: ResourcePointSpuMutation,
  ) => Promise<ResourcePointMutationResult<ResourcePointSpuMutationErrors>>;
};

/** 新增或编辑考察点 SPU 的右侧抽屉。 */
export function ResourcePointSpuDrawer({
  state,
  scope = "all",
  pointTypes,
  pending,
  errorMessage,
  geoPending,
  geoStatus,
  geoResult,
  geoOwnerSpuId,
  onClose,
  onAfterClose,
  onClearError,
  onHydrateGeo,
  onVerifyGeo,
  onAdoptGeo,
  onCreate,
  onUpdate,
}: ResourcePointSpuDrawerProps) {
  const [payload, setPayload] = useState<ResourcePointSpuMutation>(emptyResourcePointSpuMutation);
  const [errors, setErrors] = useState<ResourcePointSpuMutationErrors>({});
  const [imageUploading, setImageUploading] = useState(false);
  const [polishPending, setPolishPending] = useState(false);
  const editingSpu = state?.type === "edit" ? state.spu : undefined;
  const entityLabel = scopeEntityLabel(scope);
  const createTitle = `新增${entityLabel}`;
  const editTitle = `编辑${entityLabel}`;
  const nameLabel = `${entityLabel}名称`;
  const shortNameLabel = `${entityLabel}简称`;
  const isChina = payload.country.trim() === "中国";
  const hidePointTypeField = scope !== "all";
  const hidePointSubTypeField = scope !== "all" && scope !== "other";
  const pointSubTypeRequired = state?.type === "create"
    && !hidePointSubTypeField
    && (scope === "other" || payload.pointType.trim() === "考察点");
  const typeOptions = useMemo(
    () => Array.from(new Set([
      ...pointTypes,
      ...(payload.pointType ? [payload.pointType] : []),
    ])).map((value) => ({ label: value, value })),
    [payload.pointType, pointTypes],
  );
  const subTypeOptions = useMemo(
    () => preserveCurrentOption(resourcePointSubTypeOptions, payload.pointSubType),
    [payload.pointSubType],
  );
  const provinceOptions = preserveCurrent(getProvinceOptions(), payload.province);
  const cityOptions = preserveCurrent(getCityOptions(payload.province ?? ""), payload.city);
  const districtOptions = preserveCurrent(
    getDistrictOptions(payload.province ?? "", payload.city ?? ""),
    payload.district,
  );

  useEffect(() => {
    if (!state) return;
    setErrors({});
    setImageUploading(false);
    setPolishPending(false);
    onClearError();
    onHydrateGeo(state.type === "edit" ? state.spu : undefined);
    const base = state.type === "create"
      ? applyScopeLockedSpuDefaults(scope, {
        ...emptyResourcePointSpuMutation,
        ...((scope === "all" || scope === "other") ? { pointSubType: "1" } : {}),
      })
      : toResourcePointSpuMutation(state.spu);
    const liveGeo = state.type === "edit" && geoOwnerSpuId === state.spu.resourcePointSpuId;
    setPayload(liveGeo ? fillResourcePointMutationFromGeo(base, geoStatus, geoResult) : base);
  }, [scope, state]);

  /** 关闭弹窗并清理本地与运行时错误。 */
  function handleClose() {
    if (pending || imageUploading || polishPending) return;
    setErrors({});
    onClearError();
    onClose();
  }

  /** 润色用户已填写的简称文本，成功后回填简称文本域。 */
  async function handlePolishShortName() {
    if (polishPending || pending || imageUploading) return;
    const pointShortName = (payload.pointShortName ?? "").trim();
    if (!pointShortName) {
      Message.warning(`请先填写${shortNameLabel}后再进行 AI 润色`);
      return;
    }
    setPolishPending(true);
    try {
      const polished = await polishResourcePointShortName(pointShortName);
      setPayload((current) => ({ ...current, pointShortName: polished }));
      Message.success("简称已润色，请确认后保存");
    } catch (error) {
      Message.error(resourcePointMutationErrorMessage(error));
    } finally {
      setPolishPending(false);
    }
  }

  /** 提交当前 SPU 表单并仅在成功后关闭。 */
  async function handleOk() {
    if (!state || pending || imageUploading || polishPending) return;
    const result = state.type === "create"
      ? await onCreate(payload)
      : await onUpdate(state.spu, payload);
    if (!result.ok) {
      setErrors(result.errors ?? {});
      return;
    }
    setErrors({});
    onClose();
  }

  /** 用户触发 GEO 后：已确认按结论回填；待修正则草稿回填最相似候选。 */
  async function handleVerifyGeo() {
    if (!state || geoPending) return;
    const result = await onVerifyGeo(editingSpu, payload);
    if (!result) return;
    if (result.status === "confirmed") {
      setPayload((current) => applyResourcePointConfirmedGeo(current, result));
      return;
    }
    if (result.status !== "needs_correction") return;
    const draftCandidate = result.candidates[0];
    if (draftCandidate) {
      setPayload((current) => applyResourcePointGeoCandidate(current, draftCandidate));
      return;
    }
    if (result.amapStandardAddress || result.district) {
      setPayload((current) => applyResourcePointConfirmedGeo(current, result));
    }
  }

  /** 把待修正结果中的人工选中候选回填到当前表单，并标记本次可保存为已识别。 */
  function handleSelectGeoCandidate(candidate: GeoBusinessCandidate) {
    onAdoptGeo();
    setPayload((current) => applyResourcePointGeoCandidate(current, candidate));
  }

  return (
    <Drawer
      {...({
        role: "dialog",
        "aria-label": state?.type === "edit" ? editTitle : createTitle,
        "aria-modal": "true",
      } as Record<string, string>)}
      className="resource-point-spu-drawer"
      title={state?.type === "edit" ? editTitle : createTitle}
      visible={Boolean(state)}
      placement="right"
      width="40%"
      autoFocus
      focusLock
      unmountOnExit
      escToExit={!pending && !imageUploading && !polishPending}
      maskClosable={!pending && !imageUploading && !polishPending}
      closeIcon={<Button type="text" size="mini" aria-label="关闭" disabled={pending || imageUploading || polishPending} icon={<X size={16} />} />}
      footer={(
        <Button className="resource-point-drawer__save" type="primary" long loading={pending || imageUploading} disabled={pending || imageUploading || polishPending} onClick={handleOk}>
          {imageUploading ? "图片上传中..." : pending ? "保存中..." : "保存"}
        </Button>
      )}
      onCancel={handleClose}
      afterClose={onAfterClose}
    >
      <div className="resource-point-drawer__body">
        {errorMessage ? <div className="resource-point-error" role="alert">{errorMessage}</div> : null}
        <GeoOperationPanel
          key={editingSpu?.resourcePointSpuId ?? "create"}
          status={geoStatus}
          result={geoResult}
          onRetry={() => void handleVerifyGeo()}
          onSelectCandidate={handleSelectGeoCandidate}
        />
        <div className="resource-point-drawer__form">
          {hidePointTypeField ? null : (
            <ResourcePointFormRow label="考察点类型" required error={errors.pointType}>
              <Select
                allowCreate
                showSearch
                aria-label="考察点类型"
                options={typeOptions}
                value={payload.pointType}
                onChange={(pointType) => setPayload({ ...payload, pointType })}
              />
            </ResourcePointFormRow>
          )}
          {hidePointSubTypeField ? null : (
            <ResourcePointFormRow label="考察点二级类型" required={pointSubTypeRequired} error={errors.pointSubType}>
              <Select
                allowClear={!pointSubTypeRequired}
                aria-label="考察点二级类型"
                options={subTypeOptions}
                value={payload.pointSubType}
                onChange={(pointSubType) => setPayload({ ...payload, pointSubType })}
              />
            </ResourcePointFormRow>
          )}
          <ResourcePointFormRow label={nameLabel} required error={errors.pointName}>
            <Input aria-label={nameLabel} value={payload.pointName} onChange={(pointName) => setPayload({ ...payload, pointName })} />
          </ResourcePointFormRow>
          <ResourcePointFormRow
            label={shortNameLabel}
            error={errors.pointShortName}
            accessory={(
              <Button
                className="resource-point-short-name-field__polish"
                type="outline"
                size="mini"
                aria-label={`AI 润色${shortNameLabel}`}
                title={`AI 润色${shortNameLabel}`}
                loading={polishPending}
                disabled={pending || imageUploading || polishPending}
                icon={<Sparkles size={14} />}
                onClick={() => void handlePolishShortName()}
              />
            )}
          >
            <Input.TextArea
              aria-label={shortNameLabel}
              className="resource-point-short-name-field__input"
              autoSize={{ minRows: 2, maxRows: 4 }}
              maxLength={150}
              value={payload.pointShortName ?? ""}
              onChange={(pointShortName) => setPayload({ ...payload, pointShortName })}
            />
          </ResourcePointFormRow>
          <ResourcePointFormRow label="头图" error={errors.headImageUrl}>
            <ResourcePointImageField
              value={payload.headImageUrl}
              onChange={(headImageUrl) => setPayload((current) => ({ ...current, headImageUrl }))}
              onUploadingChange={setImageUploading}
            />
          </ResourcePointFormRow>
          <ResourcePointFormRow label="国家" required error={errors.country}>
            <Select
              allowCreate
              showSearch
              aria-label="国家"
              data-testid="resource-point-country"
              options={preserveCurrent(["中国"], payload.country).map((value) => ({ label: value, value }))}
              value={payload.country}
              onChange={(country) => setPayload({ ...payload, country, province: "", city: "", district: "" })}
            />
          </ResourcePointFormRow>
          {isChina ? (
            <>
              <ResourcePointFormRow label="省份" error={errors.province || errors.region}>
                <Select
                  allowClear
                  showSearch
                  aria-label="省份"
                  data-testid="resource-point-province"
                  options={provinceOptions.map((value) => ({ label: value, value }))}
                  value={payload.province}
                  onChange={(province) => setPayload({ ...payload, province, city: "", district: "" })}
                />
              </ResourcePointFormRow>
              <ResourcePointFormRow label="城市" error={errors.city}>
                <Select
                  allowClear
                  showSearch
                  aria-label="城市"
                  data-testid="resource-point-city"
                  disabled={!payload.province}
                  options={cityOptions.map((value) => ({ label: value, value }))}
                  value={payload.city}
                  onChange={(city) => setPayload({ ...payload, city, district: "" })}
                />
              </ResourcePointFormRow>
              <ResourcePointFormRow label="区县" error={errors.district}>
                <Select
                  allowClear
                  showSearch
                  aria-label="区县"
                  data-testid="resource-point-district"
                  disabled={!payload.city}
                  options={districtOptions.map((value) => ({ label: value, value }))}
                  value={payload.district}
                  onChange={(district) => setPayload({ ...payload, district })}
                />
              </ResourcePointFormRow>
            </>
          ) : (
            <>
              <ResourcePointFormRow label="省份" error={errors.province}>
                <Input aria-label="省份" value={payload.province ?? ""} onChange={(province) => setPayload({ ...payload, province, city: "", district: "" })} />
              </ResourcePointFormRow>
              <ResourcePointFormRow label="城市" error={errors.city}>
                <Input aria-label="城市" value={payload.city ?? ""} onChange={(city) => setPayload({ ...payload, city, district: "" })} />
              </ResourcePointFormRow>
              <ResourcePointFormRow label="区县" error={errors.district}>
                <Input aria-label="区县" value={payload.district ?? ""} onChange={(district) => setPayload({ ...payload, district })} />
              </ResourcePointFormRow>
            </>
          )}
          <ResourcePointFormRow label="详细地址" error={errors.address}>
            <Input aria-label="详细地址" value={payload.address ?? ""} onChange={(address) => setPayload({ ...payload, address })} />
          </ResourcePointFormRow>
          <Button
            type="outline"
            loading={geoPending}
            disabled={geoPending || !payload.pointName.trim()}
            onClick={handleVerifyGeo}
          >
            {geoPending ? "GEO 识别中..." : "GEO 识别并回填"}
          </Button>
          <ResourcePointFormRow label="经度" error={errors.lng}>
            <InputNumber aria-label="经度" disabled={Boolean(editingSpu)} precision={6} value={payload.lng} onChange={(lng) => setPayload({ ...payload, lng: finiteNumber(lng) })} />
          </ResourcePointFormRow>
          <ResourcePointFormRow label="纬度" error={errors.lat}>
            <InputNumber aria-label="纬度" disabled={Boolean(editingSpu)} precision={6} value={payload.lat} onChange={(lat) => setPayload({ ...payload, lat: finiteNumber(lat) })} />
          </ResourcePointFormRow>
          <ResourcePointFormRow label="参观方式" error={errors.visitMode}>
            <Input aria-label="参观方式" value={payload.visitMode ?? ""} onChange={(visitMode) => setPayload({ ...payload, visitMode })} />
          </ResourcePointFormRow>
          <ResourcePointFormRow label="教学标签" error={errors.teachingTags}>
            <Input.TextArea aria-label="教学标签" autoSize={{ minRows: 2, maxRows: 4 }} value={payload.teachingTags ?? ""} onChange={(teachingTags) => setPayload({ ...payload, teachingTags })} />
          </ResourcePointFormRow>
          <ResourcePointFormRow label="停用" error={errors.isDisabled}>
            <Switch aria-label="停用" checked={payload.isDisabled} onChange={(isDisabled) => setPayload({ ...payload, isDisabled })} />
          </ResourcePointFormRow>
          <ResourcePointFormRow label="状态" error={errors.status}>
            <Select aria-label="状态" options={[...resourcePointStatusOptions]} value={payload.status} onChange={(status) => setPayload({ ...payload, status })} />
          </ResourcePointFormRow>
          <ResourcePointFormRow label="备注" error={errors.remark}>
            <Input.TextArea aria-label="备注" autoSize={{ minRows: 3, maxRows: 6 }} value={payload.remark} onChange={(remark) => setPayload({ ...payload, remark })} />
          </ResourcePointFormRow>
        </div>
        {editingSpu ? (
          <Descriptions
            className="resource-point-drawer__details"
            title="只读详情"
            border
            size="small"
            column={1}
            tableLayout="fixed"
            data={spuDescriptionData(editingSpu, entityLabel)}
          />
        ) : null}
      </div>
    </Drawer>
  );
}

/** 生成编辑态只读审计与系统维护字段。 */
function spuDescriptionData(spu: ResourcePointSpuItem, entityLabel: string) {
  const data = [
    { label: "考察点类型", value: detailValue(spu.pointType), readOnly: false },
    { label: "考察点二级类型", value: optionLabel(resourcePointSubTypeOptions, spu.pointSubType), readOnly: false },
    { label: `${entityLabel}名称`, value: detailValue(spu.pointName), readOnly: false },
    { label: `${entityLabel}简称`, value: detailValue(spu.pointShortName), readOnly: false },
    { label: "头图", value: detailValue(spu.headImageUrl), readOnly: false },
    { label: "国家", value: detailValue(spu.country), readOnly: false },
    { label: "省份", value: detailValue(spu.province), readOnly: false },
    { label: "城市", value: detailValue(spu.city), readOnly: false },
    { label: "区县", value: detailValue(spu.district), readOnly: false },
    { label: "详细地址", value: detailValue(spu.address), readOnly: false },
    { label: "经度", value: detailValue(spu.lng), readOnly: false },
    { label: "纬度", value: detailValue(spu.lat), readOnly: false },
    { label: "参观方式", value: detailValue(spu.visitMode), readOnly: false },
    { label: "教学标签", value: detailValue(spu.teachingTags), readOnly: false },
    { label: "停用", value: spu.isDisabled ? "已停用" : "未停用", readOnly: false },
    { label: "状态", value: optionLabel(resourcePointStatusOptions, spu.status), readOnly: false },
    { label: "备注", value: detailValue(spu.remark), readOnly: false },
    { label: "SPU ID", value: detailValue(spu.resourcePointSpuId), readOnly: true },
    { label: "旧系统 ID", value: detailValue(spu.legacyId), readOnly: true },
    { label: "默认参观时长", value: spu.defaultVisitDuration === undefined ? "-" : `${spu.defaultVisitDuration} 分钟`, readOnly: true },
    { label: "适用专业", value: detailValue(spu.suitableMajors), readOnly: true },
    { label: "专业范围", value: detailValue(spu.suitableMajorScope), readOnly: true },
    { label: "AI 标记", value: spu.isAiCreated ? "AI 创建" : "人工创建", readOnly: true },
    { label: "未成团行程", value: detailValue(spu.itineraryCount), readOnly: true },
    { label: "已成团行程", value: detailValue(spu.orderItineraryCount), readOnly: true },
    { label: "创建时间", value: formatResourcePointDateTime(spu.createdAt), readOnly: true },
    { label: "创建人", value: detailValue(spu.createdBy), readOnly: true },
    { label: "更新时间", value: formatResourcePointDateTime(spu.updatedAt), readOnly: true },
    { label: "更新人", value: detailValue(spu.updatedBy), readOnly: true },
    { label: "SKU 数", value: detailValue(spu.skuCount), readOnly: true },
  ];
  return data
    .filter((item) => item.readOnly)
    .map(({ label, value }) => ({ label, value }));
}

/** 返回枚举配置的中文标签，未知或空值按详情规则显示。 */
function optionLabel(
  options: ReadonlyArray<{ value: string; label: string }>,
  value?: string,
) {
  return options.find((option) => option.value === value)?.label
    ?? (value ? `未知（${value}）` : "-");
}

/** 将详情空值统一显示为短横线，同时保留 0。 */
function detailValue(value?: string | number | null) {
  return value === undefined || value === null || value === "" ? "-" : String(value);
}

/** 保留不在当前事实源中的既有值，避免编辑时静默丢失。 */
function preserveCurrent(options: string[], current?: string) {
  return Array.from(new Set([...options, ...(current ? [current] : [])]));
}

/** 将数据库中的既有未知值作为只在当前编辑上下文可选的保留项。 */
function preserveCurrentOption(
  options: ReadonlyArray<{ value: string; label: string }>,
  current?: string,
) {
  if (!current || options.some((option) => option.value === current)) {
    return [...options];
  }
  return [...options, { value: current, label: `未知（${current}）` }];
}

/** 将 InputNumber 空值归一为 undefined。 */
function finiteNumber(value: unknown) {
  return typeof value === "number" && Number.isFinite(value) ? value : undefined;
}
